/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  legalPagesService,
  type AddLegalPage,
} from "@src/@api-services/leagal-pages";
import type { ILegalPage, ILegalPageSearchOptions } from "../types";

interface LegalPagesState {
  data: ILegalPage[];
  total: number;
  params: ILegalPageSearchOptions;
  selectedLegalPage: ILegalPage | null;
  loading: boolean;
  detailLoading: boolean;
  actionLoading: boolean;
  error: string | null;
}

export const getLegalPages = createAsyncThunk(
  "legalPages/getLegalPages",
  async (params: ILegalPageSearchOptions, { rejectWithValue }) => {
    try {
      const response = await legalPagesService.getLegalPages(params);
      return {
        params,
        data: response.data.data.data as ILegalPage[],
        total: response.data.data.totalRecord as number,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch legal pages"
      );
    }
  }
);

export const getLegalPageDetails = createAsyncThunk(
  "legalPages/getLegalPageDetails",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await legalPagesService.getLegalPageDetails(id);
      return response.data.data as ILegalPage;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch legal page details"
      );
    }
  }
);

export const addLegalPage = createAsyncThunk(
  "legalPages/addLegalPage",
  async (data: AddLegalPage, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await legalPagesService.addLegalPage(data);
      const state = getState() as any;
      await dispatch(getLegalPages(state.legalPages.params));
      return response.data.data as ILegalPage;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add legal page"
      );
    }
  }
);

export const updateLegalPage = createAsyncThunk(
  "legalPages/updateLegalPage",
  async (
    {
      id,
      legalPageData,
    }: { id: string | number; legalPageData: Partial<AddLegalPage> },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await legalPagesService.updateLegalPage(id, legalPageData);
      const state = getState() as any;
      await dispatch(getLegalPages(state.legalPages.params));
      return response.data.data as ILegalPage;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update legal page"
      );
    }
  }
);

export const deleteLegalPage = createAsyncThunk(
  "legalPages/deleteLegalPage",
  async (id: number, { dispatch, getState, rejectWithValue }) => {
    try {
      await legalPagesService.deleteLegalPage(id);
      const state = getState() as any;
      await dispatch(getLegalPages(state.legalPages.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete legal page"
      );
    }
  }
);

const initialState: LegalPagesState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "updatedAt",
    orderDirection: "DESC",
  },
  selectedLegalPage: null,
  loading: false,
  detailLoading: false,
  actionLoading: false,
  error: null,
};

export const legalPagesSlice = createSlice({
  name: "legalPages",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedLegalPage: (state) => {
      state.selectedLegalPage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLegalPages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLegalPages.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getLegalPages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getLegalPageDetails.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getLegalPageDetails.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedLegalPage = action.payload;
      })
      .addCase(getLegalPageDetails.rejected, (state) => {
        state.detailLoading = false;
        state.selectedLegalPage = null;
      })
      .addCase(addLegalPage.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(addLegalPage.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(addLegalPage.rejected, (state) => {
        // Form page shows toast — do not flip list TableErrorState (roles pattern).
        state.actionLoading = false;
      })
      .addCase(updateLegalPage.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateLegalPage.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateLegalPage.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteLegalPage.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteLegalPage.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteLegalPage.rejected, (state) => {
        state.actionLoading = false;
      });
  },
});

export const { clearError, clearSelectedLegalPage } = legalPagesSlice.actions;

export default legalPagesSlice.reducer;
